import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Menu, X, User, LogOut, Calendar, Settings, Coins, Infinity } from 'lucide-react';
import { useState } from 'react';

export const Header = () => {
  const { user, dbUser, logout, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if user has unlimited access
  const hasUnlimited = dbUser?.unlimited_until && new Date(dbUser.unlimited_until) > new Date();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-8 h-8 bg-[#D4FF00] rounded-lg flex items-center justify-center">
              <span className="font-heading font-black text-black text-sm">PK</span>
            </div>
            <span className="font-heading font-bold text-xl hidden sm:block">ProKick</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/trainers" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-trainers"
            >
              Nos Coachs
            </Link>
            {user && (
              <>
                <Link 
                  to="/bookings" 
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="nav-bookings"
                >
                  Mes Réservations
                </Link>
                <Link 
                  to="/credits" 
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="nav-credits"
                >
                  Acheter des crédits
                </Link>
              </>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {/* Credits Display */}
            {user && dbUser && (
              <Link to="/credits" className="hidden sm:flex items-center gap-2" data-testid="credits-display">
                {hasUnlimited ? (
                  <Badge className="bg-[#D4FF00] text-black font-bold px-3 py-1">
                    <Infinity className="w-4 h-4 mr-1" />
                    Illimité
                  </Badge>
                ) : (
                  <Badge variant="outline" className="font-bold px-3 py-1">
                    <Coins className="w-4 h-4 mr-1" />
                    {dbUser.credits?.toFixed(1) || '0'} h
                  </Badge>
                )}
              </Link>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium">
                      {dbUser?.display_name || user.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{dbUser?.display_name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    <div className="mt-2">
                      {hasUnlimited ? (
                        <Badge className="bg-[#D4FF00] text-black text-xs">
                          <Infinity className="w-3 h-3 mr-1" />
                          Accès Illimité
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <Coins className="w-3 h-3 mr-1" />
                          {dbUser?.credits?.toFixed(1) || '0'} heures
                        </Badge>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/credits" className="cursor-pointer" data-testid="menu-credits">
                      <Coins className="mr-2 h-4 w-4" />
                      Acheter des crédits
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/bookings" className="cursor-pointer" data-testid="menu-bookings">
                      <Calendar className="mr-2 h-4 w-4" />
                      Mes Réservations
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="menu-admin">
                        <Settings className="mr-2 h-4 w-4" />
                        Administration
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout} 
                    className="cursor-pointer text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-sm" data-testid="login-btn">
                    Connexion
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-[#D4FF00] text-black hover:bg-[#bfe600] font-bold uppercase tracking-wide text-sm"
                    data-testid="register-btn"
                  >
                    S'inscrire
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            {user && dbUser && (
              <div className="px-4 py-2 mb-2">
                {hasUnlimited ? (
                  <Badge className="bg-[#D4FF00] text-black font-bold">
                    <Infinity className="w-4 h-4 mr-1" />
                    Accès Illimité
                  </Badge>
                ) : (
                  <Badge variant="outline" className="font-bold">
                    <Coins className="w-4 h-4 mr-1" />
                    {dbUser.credits?.toFixed(1) || '0'} heures disponibles
                  </Badge>
                )}
              </div>
            )}
            <nav className="flex flex-col gap-2">
              <Link 
                to="/trainers" 
                className="px-4 py-2 text-sm font-medium hover:bg-secondary rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Nos Coachs
              </Link>
              {user && (
                <>
                  <Link 
                    to="/bookings" 
                    className="px-4 py-2 text-sm font-medium hover:bg-secondary rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mes Réservations
                  </Link>
                  <Link 
                    to="/credits" 
                    className="px-4 py-2 text-sm font-medium hover:bg-secondary rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Acheter des crédits
                  </Link>
                </>
              )}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="px-4 py-2 text-sm font-medium hover:bg-secondary rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
